
import Component from 'vue-class-component';
import Vue from 'vue';
import { Prop, Watch } from 'vue-property-decorator';
import { ModalState } from '@/core/types/modal-state';
import { ProfileAPIService } from '@/profile/services/api';
import { PinValidation, emailValidation } from '@kasasa/kloans-common-ui/dist/validations';
import { Alert } from '@/core/types/alert';
import { AlertTypeEnum } from '@/core/types/alert-type-enum';
import { CoreHelper } from '@/core/helper';
import { namespace } from 'vuex-class';

const coreNamespace = namespace('profile');

const EMAIL_VERIFICATION_SENT_SUCCESS_MESSAGE = 'We emailed a verification link to your new address. This link expires in 1 hour. Note that your email address will not be changed in our system unless the link is clicked.';
const PIN_VERIFY_UNSUCCESSFUL_MESSAGE = 'Invalid Pin';
const EMAIL_NOT_AVAILABLE_MESSAGE = (email: string) => `${email} is already in use.`;

@Component({
	validations: {
		pin: PinValidation.validations(),
		email: emailValidation.validations,
	},
})
export default class ProfileEditPasswordModal extends Vue {
	public alert: Alert | null = null;

	public email = '';

	@coreNamespace.Action('getAndSetSession')
	public getAndSetSession!: () => Promise<void>;

	public isChangeEmailLoading = false;

	public isCheckPinLoading = false;

	public isPinValid = false;

	public isShown = false;

	public pin = '';

	@Prop({ default: { isShown: false } })
	public state!: ModalState;

	@Watch('state')
	public watchState(state: { isShown: boolean }): void {
		this.isShown = state.isShown;
	}

	public wasVerificationSent: boolean = false;

	get emailErrors(): string[] {
		return emailValidation.errors(this, 'email');
	}

	get pinErrors(): string[] {
		return PinValidation.errors(this, 'pin', 4);
	}

	public close(): void {
		this.isShown = false;
		this.reset();
	}

	public async validatePin() {
		if (this.$v.pin.$invalid) {
			return;
		}

		this.alert = null;
		this.isPinValid = false;
		this.isCheckPinLoading = true;

		try {
			const { success } = await ProfileAPIService.validatePin(this.pin);

			if (success) {
				this.isPinValid = success;

				await this.getAndSetSession();
			} else {
				this.alert = {
					type: AlertTypeEnum.ERROR,
					message: PIN_VERIFY_UNSUCCESSFUL_MESSAGE,
				};
			}
		} catch (error) {
			console.error(error);

			this.alert = {
				type: AlertTypeEnum.ERROR,
				message: CoreHelper.getHttpErrorMessage(error),
			};
		}

		this.isCheckPinLoading = false;
	}

	public async sendVerificationLink(): Promise<void> {
		if (this.$v.email.$invalid) {
			return;
		}

		this.alert = null;
		this.isChangeEmailLoading = true;
		this.wasVerificationSent = false;

		try {
			const isEmailAvailable = await ProfileAPIService.checkIsEmailAvailable(this.email);

			if (!isEmailAvailable) {
				this.alert = {
					type: AlertTypeEnum.ERROR,
					message: EMAIL_NOT_AVAILABLE_MESSAGE(this.email),
				};
			} else {
				await ProfileAPIService.triggerEmailChange(this.email);

				this.wasVerificationSent = true;

				this.alert = {
					type: AlertTypeEnum.SUCCESS,
					message: EMAIL_VERIFICATION_SENT_SUCCESS_MESSAGE,
				};
			}
		} catch (error) {
			this.alert = {
				type: AlertTypeEnum.ERROR,
				message: CoreHelper.getHttpErrorMessage(error),
			};
		}

		this.isChangeEmailLoading = false;
	}

	private reset(): void {
		this.alert = null;
		this.email = '';
		this.isChangeEmailLoading = false;
		this.isCheckPinLoading = false;
		this.isPinValid = false;
		this.pin = '';
		this.wasVerificationSent = false;
		this.$v.$reset();
	}
}
